import { classNames } from "primereact/utils";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "primereact/button";
import { Ripple } from "primereact/ripple";
import { OverlayPanel } from 'primereact/overlaypanel';
import { RadioButton, RadioButtonChangeEvent } from 'primereact/radiobutton';
import type { ColorScheme } from "@/types";
import { PrimeReactContext } from "primereact/api";
import { useContext, useRef, useState, useEffect, useCallback } from "react";
import { LayoutContext } from "../../admin/layout/context/layoutcontext";
import waterLogo from "../images/amanziLytics.png";
import waterLogoDark from "../images/amanziLytics.png";

interface NavItemsProps {id: string; label: string}
interface AppBarProps {
  scrolled: boolean;
  navItems: NavItemsProps[]
}

const AppBar: React.FC<AppBarProps> = ({ scrolled, navItems = [] }) => {
  const navigate = useNavigate();
  const menuRef = useRef<HTMLElement | null>(null);
  const themeSettingsRef = useRef<OverlayPanel>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const { changeTheme } = useContext(PrimeReactContext);
  const { layoutConfig, setLayoutConfig, colors } = useContext(LayoutContext);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobileView, setIsMobileView] = useState(false);


  // Check for mobile view on mount and resize
  useEffect(() => {
    const checkMobileView = () => {
      setIsMobileView(window.innerWidth < 768); // md breakpoint
    };
    
    checkMobileView();
    window.addEventListener('resize', checkMobileView);
    
    return () => window.removeEventListener('resize', checkMobileView);
  }, []);

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isMobileMenuOpen &&
        mobileMenuRef.current &&
        !mobileMenuRef.current.contains(event.target as Node) &&
        menuRef.current &&
        !menuRef.current.contains(event.target as Node)
      ) {
        setIsMobileMenuOpen(false);
      }
    };

    // Close on escape key press
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isMobileMenuOpen) {
        setIsMobileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscapeKey);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isMobileMenuOpen]);

  // Close mobile menu when switching to desktop view
  useEffect(() => {
    if (!isMobileView && isMobileMenuOpen) {
      setIsMobileMenuOpen(false);
    }
  }, [isMobileView, isMobileMenuOpen]);

  const changeColorScheme = (colorScheme: ColorScheme) => {
    changeTheme?.(
      layoutConfig.colorScheme,
      colorScheme,
      "theme-link",
      () => {
        setLayoutConfig((prevState) => ({ ...prevState, colorScheme }));
        localStorage.setItem("colorScheme", colorScheme);
      }
    );
  };

  const handleNavLinkClick = useCallback((e: React.MouseEvent, sectionId: string) => {
    e.preventDefault();
    
    // Close mobile menu if open
    if (isMobileMenuOpen) {
      setIsMobileMenuOpen(false);
    }
    
    setTimeout(() => {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  }, [isMobileMenuOpen]);

  const handleMobileMenuToggle = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleNavigation = (path: string) => {
    // Close mobile menu if open
    if (isMobileMenuOpen) {
      setIsMobileMenuOpen(false);
    }
    navigate(path);
  };

  // Close mobile menu when clicking on overlay
  const handleOverlayClick = () => {
    setIsMobileMenuOpen(false);
  };

  // Inline styles for mobile menu
  const mobileMenuStyles = `
    @keyframes slideDown {
      from {
        opacity: 0;
        transform: translateY(-10px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }
    
    @keyframes fadeIn {
      from {
        opacity: 0;
      }
      to {
        opacity: 0.5;
      }
    }
  `;

  return (
    <>
      <style>{mobileMenuStyles}</style>
      <header 
        className={`fixed top-0 left-0 right-0 z-1 transition-all duration-300 ${scrolled ? 'shadow-4' : ''}`}
        style={{ 
          backgroundColor: colors.navbarBg,
          borderBottom: `1px solid ${colors.borderColor}`,
          backdropFilter: 'blur(10px)'
        }}
      >
        <div className="flex align-items-center justify-content-between py-3 px-4 mx-0 md:px-7 lg:px-8 lg:mx-8">
          <Link to="/" className="cursor-pointer flex align-items-center gap-3">
            <img 
              src={layoutConfig.colorScheme === "dark" ? waterLogoDark : waterLogo} 
              alt="AmanziLytics Logo"
              style={{ height: "40px" }}
            />
          </Link>

          {/* Mobile Menu Toggle */}
          <button
            ref={menuRef as any}
            className="pi pi-bars text-3xl cursor-pointer block md:hidden p-2 border-none bg-transparent"
            style={{ color: colors.textColor }}
            onClick={handleMobileMenuToggle}
            aria-label="Toggle menu"
          />

          {/* Desktop Navigation */}
          <nav className="align-items-center flex-grow-1 hidden md:flex md:static w-full md:px-0">
            <ul className="list-none p-0 m-0 ml-auto flex md:align-items-center select-none flex-row">
              <li className="mx-3">
                  <div
                   
                    className="font-medium p-2 hover:text-primary transition-colors border-none bg-transparent text-decoration-none"
                    style={{ color: colors.textColor, cursor: 'pointer' }}
                    onClick={() => navigate("/")}
                  >
                    Home
                  </div>
                </li>
              {navItems.map((item) => (
                <li key={item.id} className="mx-3">
                  <a
                    href={`#${item.id}`}
                    className="font-medium p-2 hover:text-primary transition-colors border-none bg-transparent text-decoration-none"
                    style={{ color: colors.textColor }}
                    onClick={(e) => handleNavLinkClick(e, item.id)}
                  >
                    {item.label}
                  </a>
                </li>
              ))}
              <li className="mx-3">
                  <div
                   
                    className="font-medium p-2 hover:text-primary transition-colors border-none bg-transparent text-decoration-none"
                    style={{ color: colors.textColor, cursor: 'pointer' }}
                    onClick={() => navigate("/contact-us")}
                  >
                    Contact Us
                  </div>
                </li>
                <li className="mx-3">
                  <div
                   
                    className="font-medium p-2 hover:text-primary transition-colors border-none bg-transparent text-decoration-none"
                    style={{ color: colors.textColor, cursor: 'pointer' }}
                    onClick={() => navigate("/about-us")}
                  >
                    About Us
                  </div>
                </li>
              <li className="mx-3">
                <Button 
                  label="DEMO" 
                  className="p-button-outlined font-medium border-2"
                  style={{ 
                    borderColor: colors.primaryColor, 
                    color: colors.primaryColor,
                    borderWidth: '2px'
                  }}
                  onClick={() => navigate("/demo")}
                />
              </li>
              <li className="mx-3">
                <Button 
                  label="LOGIN" 
                  className="font-medium"
                  style={{ 
                    backgroundColor: colors.primaryColor, 
                    borderColor: colors.primaryColor 
                  }}
                  // onClick={() => navigate("/login")}
                />
              </li>
              
              {/* Theme Settings */}
              <li className="ml-3">
                <Button
                  type="button"
                  icon="pi pi-palette"
                  text
                  rounded
                  severity="secondary"
                  className="flex-shrink-0"
                  style={{ color: colors.textColor }}
                  onClick={(e) => themeSettingsRef?.current?.toggle(e)}
                />
                <OverlayPanel ref={themeSettingsRef} className="shadow-4">
                  <div className="flex flex-column gap-3 p-2" style={{ minWidth: '150px' }}>
                    <h4 className="m-0 mb-2" style={{ color: colors.textColor }}>Theme</h4>
                    <div className="field-radiobutton">
                      <RadioButton
                        name="colorScheme"
                        value="light"
                        checked={layoutConfig.colorScheme === "light"}
                        onChange={(e: RadioButtonChangeEvent) =>
                          changeColorScheme(e.value)
                        }
                        inputId="mode-light"
                      />
                      <label htmlFor="mode-light" style={{ color: colors.textColor }}>Light</label>
                    </div>
                    <div className="field-radiobutton">
                      <RadioButton
                        name="colorScheme"
                        value="dim"
                        checked={layoutConfig.colorScheme === "dim"}
                        onChange={(e: RadioButtonChangeEvent) =>
                          changeColorScheme(e.value)
                        }
                        inputId="mode-dim"
                      />
                      <label htmlFor="mode-dim" style={{ color: colors.textColor }}>Dim</label>
                    </div>
                    <div className="field-radiobutton">
                      <RadioButton
                        name="colorScheme"
                        value="dark"
                        checked={layoutConfig.colorScheme === "dark"}
                        onChange={(e: RadioButtonChangeEvent) =>
                          changeColorScheme(e.value)
                        }
                        inputId="mode-dark"
                      />
                      <label htmlFor="mode-dark" style={{ color: colors.textColor }}>Dark</label>
                    </div>
                  </div>
                </OverlayPanel>
              </li>
            </ul>
          </nav>

          {/* Mobile Navigation Overlay */}
          {isMobileMenuOpen && (
            <div 
              className="fixed inset-0 bg-black/50 z-40 md:hidden transition-opacity duration-300"
              onClick={handleOverlayClick}
              style={{ 
                top: '64px',
                animation: 'fadeIn 0.3s ease'
              }}
            />
          )}
        </div>
      </header>
      
      {/* Mobile Navigation Menu - Separate from header for better positioning */}
      {isMobileView && (
        <div 
          ref={mobileMenuRef}
          className={classNames(
            "fixed mt-7 left-0 right-0 z-50 transition-all duration-300 transform",
            "shadow-4 overflow-hidden",
            isMobileMenuOpen 
              ? "translate-y-0 opacity-100 top-16" 
              : "-translate-y-full opacity-0 top-0 pointer-events-none"
          )} 
          style={{ 
            backgroundColor: colors.navbarBg,
            maxHeight: 'calc(100vh - 64px)',
            overflowY: 'auto',
            borderBottom: `1px solid ${colors.borderColor}`,
            animation: isMobileMenuOpen ? 'slideDown 0.3s ease' : 'none',
            backdropFilter: 'blur(10px)'
          }}
        >
          <div className="p-4">
            <ul className="list-none m-0 p-0 flex flex-column gap-2">
              <li className="mb-0">
                
                <div
                  
                  className="p-ripple flex align-items-center cursor-pointer p-1 border-round transition-colors transition-duration-200 text-decoration-none"
                  style={{ 
                    color: colors.textColor,
                    backgroundColor: 'rgba(255,255,255,0.05)',
                    textDecoration: 'none'
                  }}
                  onClick={() => navigate("/")}
                >
                  
                  <span className="font-medium" style={{ fontSize: '1rem' }}>Home</span>
                  <Ripple />
                </div>
              </li>
              
              {navItems.map((item) => (
                <li key={item.id} className="">
                  
                  <a
                    href={`#${item.id}`}
                    className="p-ripple flex align-items-center cursor-pointer p-1 border-round transition-colors transition-duration-200 text-decoration-none"
                    style={{ 
                      color: colors.textColor,
                      backgroundColor: 'rgba(255,255,255,0.05)',
                      textDecoration: 'none'
                    }}
                    onClick={(e) => handleNavLinkClick(e, item.id)}
                  >
                    
                    <span className="font-medium" style={{ fontSize: '1rem' }}>{item.label}</span>
                    <Ripple />
                  </a>
                </li>
                
              ))}
              <li className="mb-0">  
                <div
                  className="p-ripple flex align-items-center cursor-pointer p-1 border-round transition-colors transition-duration-200 text-decoration-none"
                  style={{ 
                    color: colors.textColor,
                    backgroundColor: 'rgba(255,255,255,0.05)',
                    textDecoration: 'none'
                  }}
                  onClick={() => navigate("/about-us")}
                >
                  <span className="font-medium" style={{ fontSize: '1rem' }}>About Us</span>
                  <Ripple />
                </div>
              </li>
              <li className="mb-0">  
                <div
                  className="p-ripple flex align-items-center cursor-pointer p-1 border-round transition-colors transition-duration-200 text-decoration-none"
                  style={{ 
                    color: colors.textColor,
                    backgroundColor: 'rgba(255,255,255,0.05)',
                    textDecoration: 'none'
                  }}
                  onClick={() => navigate("/contact-us")}
                >
                  <span className="font-medium" style={{ fontSize: '1rem' }}>Contact Us</span>
                  <Ripple />
                </div>
              </li>
              
              <div className="border-top-1 my-1" style={{ borderColor: `${colors.borderColor}` }}></div>
              
              <li className="mb-0">
                <Button 
                  label="DEMO" 
                  className="w-full p-button-outlined font-medium"
                  style={{ 
                    borderColor: colors.primaryColor, 
                    color: colors.primaryColor,
                    borderWidth: '1px',
                    fontSize: '0.9rem',
                    padding: '0.6rem 1rem'
                  }}
                  onClick={() => handleNavigation("/demo")}
                />
              </li>
              <li className="mb-0">
                <Button 
                  label="LOGIN" 
                  className="w-full font-medium"
                  style={{ 
                    backgroundColor: colors.primaryColor, 
                    borderColor: colors.primaryColor,
                    fontSize: '0.9rem',
                    padding: '0.6rem 1rem'
                  }}
                  // onClick={() => handleNavigation("/login")}
                />
              </li>
              
              {/* Theme selector in mobile */}
              <li className="mt-3">
                <div className="flex align-items-center justify-content-between p-0" style={{ borderTop: `1px solid ${colors.borderColor}` }}>
                
                  <Button
                    type="button"
                    icon="pi pi-palette"
                    text
                    rounded
                    severity="secondary"
                    className="ml-0"
                    style={{ color: colors.textColor }}
                    onClick={(e) => themeSettingsRef?.current?.toggle(e)}
                  />
                </div>
              </li>
            </ul>
          </div>
        </div>
      )}
    </>
  );
};

export default AppBar;